import type { Commit, RepoState } from "../lib/types";
import { aheadBehind } from "../lib/git-sim";
import { I } from "./Icons";

interface Props {
  repo: RepoState;
  animateLast?: boolean;
  /** Which side just synced ("push" → arrow flashes right; "pull"/"fetch" → flashes left). */
  syncFlash?: "push" | "pull" | "fetch" | null;
}

const STEP = 90;
const OFFSET_X = 60;
const LANE_Y_MAIN = 110;
const LANE_Y_FEATURE = 50;

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

function laneY(branch: string): number {
  return branch === "feature" ? LANE_Y_FEATURE : LANE_Y_MAIN;
}

function commitColor(branch: string): string {
  return branch === "feature" ? "#2D9BD6" : "#FFFFFF";
}

function reachable(commits: Commit[], tips: string[]): Commit[] {
  const wanted = new Set<string>();
  for (const t of tips) {
    let cur: Commit | undefined = commits.find((c) => c.id === t);
    while (cur && !wanted.has(cur.id)) {
      wanted.add(cur.id);
      if (!cur.parent) break;
      cur = commits.find((c) => c.id === cur!.parent);
    }
  }
  return commits.filter((c) => wanted.has(c.id));
}

function edgePath(
  p: { x: number; y: number },
  me: { x: number; y: number },
): string {
  if (p.y === me.y) return `M ${p.x} ${p.y} L ${me.x} ${me.y}`;
  const midX = p.x + (me.x - p.x) * 0.55;
  return `M ${p.x} ${p.y} C ${midX} ${p.y}, ${midX} ${me.y}, ${me.x} ${me.y}`;
}

interface PanelProps {
  title: string;
  subtitle: string;
  empty: boolean;
  commits: Commit[];
  branches: Record<string, string>;
  head?: string;
  highlightLast?: boolean;
  ghostIds?: Set<string>;
}

function Panel({
  title,
  subtitle,
  empty,
  commits,
  branches,
  head,
  highlightLast,
  ghostIds,
}: PanelProps) {
  const sortedX = commits.length
    ? Math.max(...commits.map((c) => c.x), 0)
    : 0;
  const width = OFFSET_X + (sortedX + 1) * STEP + 70;
  const height = 200;

  const positions: Record<string, { x: number; y: number; color: string }> = {};
  commits.forEach((c) => {
    positions[c.id] = {
      x: OFFSET_X + c.x * STEP,
      y: laneY(c.branch),
      color: commitColor(c.branch),
    };
  });

  const lastCommitId = commits[commits.length - 1]?.id;
  const showFeature = commits.some((c) => c.branch === "feature");

  return (
    <div
      style={{
        background: "var(--nymbl-navy)",
        borderRadius: 12,
        padding: 14,
        position: "relative",
        overflow: "hidden",
        minHeight: 240,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "rgba(255,255,255,0.85)",
              textTransform: "uppercase",
              letterSpacing: "0.1em",
            }}
          >
            {title}
          </div>
        </div>
        <div
          style={{
            fontFamily: "ui-monospace, Menlo",
            fontSize: 11,
            color: "rgba(255,255,255,0.55)",
          }}
        >
          {subtitle}
        </div>
      </div>

      {empty ? (
        <div
          style={{
            height: 200,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "rgba(255,255,255,0.5)",
            border: "1px dashed rgba(255,255,255,0.18)",
            borderRadius: 8,
            gap: 8,
          }}
        >
          <I.File size={22} />
          <div style={{ fontSize: 12 }}>Empty — nothing here yet.</div>
        </div>
      ) : (
        <svg
          width="100%"
          viewBox={`0 0 ${width} ${height}`}
          style={{ display: "block" }}
        >
          <line
            x1={0}
            y1={LANE_Y_MAIN}
            x2={width}
            y2={LANE_Y_MAIN}
            stroke="rgba(255,255,255,0.08)"
            strokeWidth="1"
            strokeDasharray="2 4"
          />
          {showFeature && (
            <line
              x1={0}
              y1={LANE_Y_FEATURE}
              x2={width}
              y2={LANE_Y_FEATURE}
              stroke="rgba(255,255,255,0.08)"
              strokeWidth="1"
              strokeDasharray="2 4"
            />
          )}
          <text
            x={12}
            y={LANE_Y_MAIN + 4}
            fill="rgba(255,255,255,0.35)"
            fontSize="10"
            fontFamily="ui-monospace, Menlo"
          >
            main
          </text>
          {showFeature && (
            <text
              x={12}
              y={LANE_Y_FEATURE + 4}
              fill="rgba(45,155,214,0.55)"
              fontSize="10"
              fontFamily="ui-monospace, Menlo"
            >
              feature
            </text>
          )}

          {commits.map((c) => {
            if (!c.parent) return null;
            const p = positions[c.parent];
            const me = positions[c.id];
            if (!p || !me) return null;
            const ghost = ghostIds?.has(c.id);
            const isLast = c.id === lastCommitId && highlightLast;
            return (
              <path
                key={`e-${c.id}`}
                d={edgePath(p, me)}
                stroke={me.color}
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
                opacity={ghost ? 0.45 : 1}
                strokeDasharray={ghost ? "4 4" : undefined}
                className={isLast ? "gf-graph__edge-enter" : undefined}
              />
            );
          })}

          {commits.map((c) => {
            const pos = positions[c.id];
            if (!pos) return null;
            const ghost = ghostIds?.has(c.id);
            const isLast = c.id === lastCommitId && highlightLast;
            return (
              <g
                key={c.id}
                opacity={ghost ? 0.6 : 1}
                className={isLast ? "gf-graph__commit-enter" : undefined}
              >
                <circle
                  cx={pos.x}
                  cy={pos.y}
                  r="9"
                  fill={ghost ? "transparent" : pos.color === "#FFFFFF" ? "#fff" : pos.color}
                  stroke={c.branch === "main" ? "#fff" : pos.color}
                  strokeWidth="2"
                  strokeDasharray={ghost ? "3 3" : undefined}
                />
                <text
                  x={pos.x}
                  y={pos.y + (c.branch === "main" ? 26 : -16)}
                  textAnchor="middle"
                  fill="rgba(255,255,255,0.5)"
                  fontSize="9"
                  fontFamily="ui-monospace, Menlo"
                >
                  {c.id.slice(0, 7)}
                </text>
                <text
                  x={pos.x}
                  y={pos.y + (c.branch === "main" ? 38 : -28)}
                  textAnchor="middle"
                  fill="rgba(255,255,255,0.7)"
                  fontSize="10"
                  fontFamily="Raleway, sans-serif"
                >
                  {truncate(c.msg, 18)}
                </text>
              </g>
            );
          })}

          {Object.entries(branches).map(([name, commitId]) => {
            const pos = positions[commitId];
            if (!pos) return null;
            const isHead = head === name;
            const color = name === "feature" ? "#2D9BD6" : "#FFFFFF";
            const text = isHead ? `HEAD → ${name}` : name;
            const w = text.length * 6.5 + 14;
            return (
              <g key={`lbl-${name}`}>
                <rect
                  x={pos.x + 16}
                  y={pos.y - 10}
                  width={w}
                  height="20"
                  rx="10"
                  fill={isHead ? color : "transparent"}
                  stroke={color}
                  strokeWidth="1.5"
                />
                <text
                  x={pos.x + 16 + 8}
                  y={pos.y + 4}
                  fill={
                    isHead
                      ? color === "#FFFFFF"
                        ? "#001847"
                        : "#fff"
                      : color
                  }
                  fontSize="10"
                  fontFamily="ui-monospace, Menlo"
                  fontWeight="600"
                >
                  {text}
                </text>
              </g>
            );
          })}
        </svg>
      )}
    </div>
  );
}

export function RemoteAwareGraph({ repo, animateLast, syncFlash }: Props) {
  const origin = repo.remotes?.origin;
  if (!origin) {
    // Fallback: shouldn't happen for remote lessons.
    return null;
  }

  const localTips = Object.values(repo.branches);
  const localReachable = reachable(repo.commits, localTips);
  const originReachable = reachable(origin.commits, Object.values(origin.branches));

  const ab = aheadBehind(repo, "main");
  const localEmpty = localReachable.length === 0;

  let statusLabel = "up to date";
  let statusColor = "var(--success)";
  if (localEmpty) {
    statusLabel = "not cloned yet";
    statusColor = "var(--fg-link)";
  } else if (ab.ahead > 0 && ab.behind > 0) {
    statusLabel = `${ab.ahead} ahead · ${ab.behind} behind`;
    statusColor = "var(--warning)";
  } else if (ab.ahead > 0) {
    statusLabel = `${ab.ahead} ahead of origin/main`;
    statusColor = "var(--warning)";
  } else if (ab.behind > 0) {
    statusLabel = `${ab.behind} behind origin/main`;
    statusColor = "var(--warning)";
  }

  return (
    <div className="gf-graph">
      <div className="gf-graph__head">
        <h3>Your laptop &amp; GitHub</h3>
        <div className="gf-graph__head-meta">
          <span
            style={{
              padding: "2px 8px",
              borderRadius: 999,
              background: "rgba(255,255,255,0.06)",
              color: statusColor,
              fontWeight: 600,
              fontSize: 11,
            }}
          >
            {statusLabel}
          </span>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          gap: 10,
          padding: 14,
          alignItems: "center",
        }}
      >
        <Panel
          title="🖥  Your laptop (local)"
          subtitle={localEmpty ? "" : `branch: ${repo.head}`}
          empty={localEmpty}
          commits={localReachable}
          branches={repo.branches}
          head={repo.head}
          highlightLast={animateLast}
        />
        <SyncIndicator flash={syncFlash} />
        <Panel
          title="☁  origin (GitHub)"
          subtitle={`${originReachable.length} commit${originReachable.length === 1 ? "" : "s"}`}
          empty={originReachable.length === 0}
          commits={originReachable}
          branches={origin.branches}
        />
      </div>

      <div className="gf-graph__legend">
        <div className="gf-graph__legend-item">
          <div className="gf-graph__legend-dot" style={{ background: "#fff" }}></div>
          <span>main commit</span>
        </div>
        <div className="gf-graph__legend-item">
          <div
            className="gf-graph__legend-dot"
            style={{ background: "#2D9BD6" }}
          ></div>
          <span>feature commit</span>
        </div>
        <div className="gf-graph__legend-item" style={{ marginLeft: "auto" }}>
          <span style={{ fontFamily: "ui-monospace, Menlo" }}>
            local {localReachable.length} · origin {originReachable.length}
          </span>
        </div>
      </div>
    </div>
  );
}

function SyncIndicator({ flash }: { flash?: "push" | "pull" | "fetch" | null }) {
  const pushActive = flash === "push";
  const pullActive = flash === "pull" || flash === "fetch";
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        minWidth: 40,
      }}
    >
      <div
        style={{
          color: pushActive ? "var(--success)" : "rgba(255,255,255,0.35)",
          transition: "color 200ms",
          transform: pushActive ? "scale(1.4)" : "scale(1)",
        }}
        title="push →"
      >
        <I.ArrowRight size={20} />
      </div>
      <div
        style={{
          fontSize: 10,
          color: "rgba(255,255,255,0.4)",
          letterSpacing: "0.1em",
        }}
      >
        SYNC
      </div>
      <div
        style={{
          color: pullActive ? "var(--success)" : "rgba(255,255,255,0.35)",
          transition: "color 200ms",
          transform: pullActive ? "scale(1.4)" : "scale(1)",
        }}
        title="← pull / fetch"
      >
        <I.ChevronLeft size={20} />
      </div>
    </div>
  );
}
